import React from 'react';
import {listCalendars, listEventsWithSync, insertEvent, updateEvent, deleteEvent, patchEventAttendees} from "../GoogleAPI/GoogleCalendarAPI";
import { getGoogleAccessToken, getAccountToken } from '../Auth/UserAuth';
// Zustand store (migrated from EventsContext - GRO-19)
import { useEventsStore } from '../../stores/useEventsStore';
import useGoogleSyncStore from '../../stores/useGoogleSyncStore';
import moment from 'moment';
import useAuthStore from '../../stores/useAuthStore';
import { firestoreService } from '../../services/firestore.service';
import { auth } from '../../config/firebase';
import logger from '../../utils/logger';

// Rolling token expiry extension — re-save token to Firestore after successful API calls
// Throttled to once per 30 minutes per account to avoid excessive writes
const _lastTokenExtension = new Map();
const EXTENSION_THROTTLE_MS = 30 * 60 * 1000; // 30 minutes

const maybeExtendToken = (accountEmail, accessToken) => {
    const lastExtended = _lastTokenExtension.get(accountEmail) || 0;
    if (Date.now() - lastExtended < EXTENSION_THROTTLE_MS) return;
    _lastTokenExtension.set(accountEmail, Date.now());

    const currentUser = auth.currentUser;
    if (currentUser) {
        firestoreService.saveGoogleToken(currentUser.uid, accountEmail, accessToken).catch(() => {});
    }
};

// Deduplicate 401 handling — track accounts already flagged as expired
// to prevent redundant clearAccountToken + error spam when multiple API calls fail simultaneously
const _expiredAccountsBurst = new Set();
let _expiredBurstTimer = null;

const clearExpiredBurst = () => {
    _expiredAccountsBurst.clear();
    _expiredBurstTimer = null;
};

// Handle token expiration errors - clears the expired token to trigger re-auth
const handleTokenExpiredError = (accountEmail, error) => {
    const errorStatus = error?.status || error?.response?.status;
    const errorCode = error?.code || error?.response?.data?.error?.code;

    // Check for 401 (Unauthorized) or 403 (Forbidden - sometimes means invalid token)
    if (errorStatus === 401 || errorCode === 401 ||
        (errorStatus === 403 && error?.response?.data?.error?.message?.includes('invalid'))) {
        // Skip if already handled in this burst
        if (_expiredAccountsBurst.has(accountEmail)) {
            return { tokenExpired: true, needsReauth: true };
        }
        _expiredAccountsBurst.add(accountEmail);

        // Clear burst tracking after 30 seconds
        if (!_expiredBurstTimer) {
            _expiredBurstTimer = setTimeout(clearExpiredBurst, 30000);
        }

        // Clear the expired token from store
        const store = useAuthStore.getState();
        store.clearAccountToken(accountEmail);
        return { tokenExpired: true, needsReauth: true };
    }
    return { tokenExpired: false };
};

const isWritableCalendar = ( calendar) => {
    return (calendar.accessRole === "writer" || calendar.accessRole === "owner") ? true : false;
}

const compareCalendars = ( a, b ) => {
    const primary_a = a.primary || false;
    const primary_b = b.primary || false;
    if (primary_a || primary_b){
        if (primary_a && (!primary_b)){
            return -1;
        }
        if ((!primary_a) && primary_b){
            return 1;
        }
    }
    if (a.accessRole != b.accessRole) {
        return ( a.accessRole < b.accessRole )? -1: 1;
    }
  }

// Get access token for a specific account, with fallback to primary token
const getTokenForAccount = (accountEmail) => {
    // First try to get the specific account token
    const accountToken = getAccountToken(accountEmail);
    if (accountToken) {
        return accountToken;
    }
    // Fallback to primary token for backward compatibility
    return getGoogleAccessToken();
};

// Process sync results from Google - handles add, update, and delete
const processGoogleSyncResult = (syncResult, account, cal, syncStore, year) => {
    const { events, nextSyncToken } = syncResult;
    const eventsStore = useEventsStore.getState();

    // Separate events by status
    const deletedEvents = events.filter(e => e.status === 'cancelled');
    const activeEvents = events.filter(e => e.status !== 'cancelled');

    // Handle deleted events - remove from local store
    if (deletedEvents.length > 0) {
        const deletedIds = new Set(deletedEvents.map(e => e.id));
        eventsStore.setEvents(
            eventsStore.events.filter(e => !deletedIds.has(e.id))
        );
        import('../../services/sharing.service').then(({ unpublishEvent }) => {
            deletedEvents.forEach((deletedEvent) => {
                if (deletedEvent?.id) {
                    unpublishEvent(deletedEvent.id).catch(() => {});
                }
            });
        }).catch(() => {});
        // Clean up sync metadata for deleted events
        deletedEvents.forEach(e => syncStore.removeEventSyncMeta(e.id));
    }

    // Handle new/updated events
    if (activeEvents.length > 0) {
        eventsStore.addEventsFromProviderCalendar(account, cal, activeEvents, convertGoogleEventToGrownupsEvent);

        // Track sync metadata for events from Google
        activeEvents.forEach(event => {
            if (event.id) {
                syncStore.setEventSyncMeta(event.id, {
                    etag: event.etag,
                    lastModified: event.updated,
                    source: 'google'
                });
            }
        });

        // Fire-and-forget inbound attendee sync for shared events (Phase 3)
        const convertedEvents = activeEvents.map(convertGoogleEventToGrownupsEvent);
        import('../../services/rsvp.service').then(({ processInboundAttendeeSyncs }) => {
            processInboundAttendeeSyncs(convertedEvents).catch(() => {});
        }).catch(() => {});
    }

    // Save the sync token for next incremental sync
    if (nextSyncToken) {
        syncStore.setSyncToken(account.email, cal.id, nextSyncToken);
    }

    // Update last sync time in sync store
    syncStore.setLastSyncTime(account.email, cal.id);
};

export const getAndUpdateGoogleCalendarData = (accounts, setCalendars, setEvents, setSelectedCalendars, cachedEvents, setCachedEvents, currentDate, onTokenExpired = null) => {
    // Use incremental sync - events are cached in localStorage via Zustand persist
    // Sync tokens allow fetching only changes since last sync
    // This preserves events when navigating between views

    for (const account of accounts) {
        // Get the token for this specific account
        const userAccessToken = getTokenForAccount(account.email);

        if (!userAccessToken) {
            continue;
        }

        listCalendars(userAccessToken).then((result) => {
            // Token is working — extend its Firestore expiry (throttled)
            maybeExtendToken(account.email, userAccessToken);

            let cals = result.data.items;
            cals.sort(compareCalendars);

            // Update calendars for this specific account
            setCalendars((prevAccounts) => {
                // Remove existing calendars for this account and add updated ones
                const otherAccounts = prevAccounts.filter(a => a.email !== account.email);
                return [...otherAccounts, {
                    email: account.email,
                    provider: account.provider,
                    calendars: cals,
                }];
            });

            // setSelectedCalendars - merge with existing selections
            setSelectedCalendars((prevSelected) => ({
                ...prevSelected,
                ...Object.fromEntries(cals.map((cal) => [`${account.email}-${cal.id}`, prevSelected[`${account.email}-${cal.id}`] ?? ('selected' in cal ? cal.selected : false)]))
            }));

            // update cals
            for (const cal of cals){
                cal.isWritable = isWritableCalendar(cal)
                cal.primary_email = account.email;
                const date = currentDate ? moment(currentDate): moment()
                const year = date.year();
                const calKey = `${cal.primary_email}-${cal.id}-${year}`

                // Skip if already fetched in this session
                if (cachedEvents.includes(calKey)) {
                    continue;
                }

                // Get sync token from store for incremental sync
                const syncStore = useGoogleSyncStore.getState();
                const existingSyncToken = syncStore.getSyncToken(account.email, cal.id);

                // Use sync tokens for efficient incremental updates
                const syncOptions = {
                    syncToken: existingSyncToken,
                    timeMin: moment(currentDate).startOf('year').toDate(),
                    timeMax: moment(currentDate).endOf('year').toDate(),
                };

                listEventsWithSync(userAccessToken, cal.id, syncOptions).then((syncResult) => {
                    // Handle invalid sync token - retry with full sync
                    if (!syncResult.success && syncResult.error === 'sync_token_invalid') {
                        syncStore.clearSyncToken(account.email, cal.id);
                        // Retry without sync token
                        listEventsWithSync(userAccessToken, cal.id, {
                            timeMin: moment(currentDate).startOf('year').toDate(),
                            timeMax: moment(currentDate).endOf('year').toDate(),
                        }).then((fullSyncResult) => {
                            if (fullSyncResult.success) {
                                processGoogleSyncResult(fullSyncResult, account, cal, syncStore, year);
                            }
                        });
                        return;
                    }

                    if (!syncResult.success) {
                        logger.error(`Sync failed for ${cal.id}:`, syncResult.error);
                        return;
                    }

                    processGoogleSyncResult(syncResult, account, cal, syncStore, year);
                    setCachedEvents((prevCachedEvents) => ([...prevCachedEvents, calKey]));

                }).catch((error) => {
                    logger.error(`Failed to fetch events for calendar ${cal.id}:`, error);
                    // Check if token expired and notify
                    const expiredResult = handleTokenExpiredError(account.email, error);
                    if (expiredResult.tokenExpired && onTokenExpired) {
                        onTokenExpired(account.email);
                    }
                });
            }
        }).catch((error) => {
            logger.error(`Failed to fetch calendars for account ${account.email}:`, error);
            // Check if token expired and notify
            const expiredResult = handleTokenExpiredError(account.email, error);
            if (expiredResult.tokenExpired && onTokenExpired) {
                onTokenExpired(account.email);
            }
        });
    }

    return {};
}


/**
 * Event Property Mapping
 *
 * Grownups events use extendedProperties.private to store app-specific fields
 * (calendar, colors, task metadata, visibility) that persist through Google Calendar.
 * See privateProperties array below for the full list.
 */

const propertiesMap = [
    // google, grownups event
    ['summary', 'title'],
    ['location', 'location'],
    ['description', 'description'],
    // Note: 'id' is NOT included here - Google generates IDs on insert,
    // and for updates the ID goes in the URL, not the request body
    ['hangoutLink', 'hangoutLink']
]
const boolProperties = ['isAllDay', 'isDraggable', 'isDark', 'visible', 'checked', 'rsvpEnabled']
const numericProperties = ['maxAttendees']
const jsonProperties = ['invitedEmails']
const privateProperties = ['calendar', 'primary_email', 'calendarColor', 'calendarTextcolor', 'color', 'textcolor',  'link',
                            'isAllDay', 'isDraggable', 'isDark',
                            'isWritable', // should update based on calendar
                            'listid', 'taskid', 'type', 'checked',
                            'visible',
                            'emoji', 'image', 'vibe', 'font', 'category',
                            // Sharing fields
                            'visibility', 'slug', 'inviteToken',
                            'hostId', 'hostDisplayName', 'hostPhotoURL', 'hostEmail',
                            'rsvpEnabled', 'maxAttendees', 'shareCardStyle',
                            'invitedEmails',
                            'createdAt', 'updatedAt']

const serializePrivateValue = (prop, value) => {
    if (value === undefined || value === null) return value;
    if (jsonProperties.includes(prop)) {
        try {
            return JSON.stringify(value);
        } catch {
            return '[]';
        }
    }
    return value;
}

const parsePrivateValue = (prop, value) => {
    if (!jsonProperties.includes(prop)) return value;
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string') return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}
/**
 * Convert a Grownups event to Google Calendar event format.
 * Maps: title→summary, location, description, hangoutLink, start/end times.
 * Stores app-specific properties in extendedProperties.private.
 * @see https://developers.google.com/calendar/api/v3/reference/events/insert
 */
export const convertGrownupsEventToGoogleEvent = (event) => {
    let googleEvent = {}

    for (const prop of propertiesMap) {
        if (Object.hasOwn(event, prop[1]) && event[prop[1]]) {
            googleEvent[prop[0]] = event[prop[1]]
        }
    }
    
    if (event.isAllDay){
        // For all-day events, format as YYYY-MM-DD using LOCAL date components
        // This ensures the date displayed matches what we send to Google
        const startDate = new Date(event.start);
        const endDate = new Date(event.end);

        // Format as YYYY-MM-DD using local date components
        const formatLocalDate = (d) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        googleEvent.start = {'date': formatLocalDate(startDate)};
        // Google expects exclusive end dates - if event ends at midnight of day N,
        // that's the start of day N, which is correct for exclusive end
        googleEvent.end = {'date': formatLocalDate(endDate)};
    }
    else {
        googleEvent.start = {'dateTime': moment(event.start).toISOString()}
        googleEvent.end = {'dateTime': moment(event.end).toISOString()}
    }

    googleEvent.extendedProperties = {
        private: Object.fromEntries(
            privateProperties
                .filter(p => Object.hasOwn(event, p))
                .map((prop) => [prop, serializePrivateValue(prop, event[prop])])
        )
    }

    return googleEvent;
}

/**
 * Convert a Google Calendar event to Grownups event format.
 * Maps: summary→title, location, description, hangoutLink, start/end times.
 * Restores app-specific properties from extendedProperties.private.
 * @see https://developers.google.com/calendar/api/v3/reference/events
 */
export const convertGoogleEventToGrownupsEvent = (googleEvent) => {
    let event = {}

    for (const prop of propertiesMap) {
        if (Object.hasOwn(googleEvent, prop[0]) && googleEvent[prop[0]]) {
            event[prop[1]] = googleEvent[prop[0]]

        }
    }

    // Always copy the id from Google events (needed for deduplication)
    if (googleEvent.id) {
        event.id = googleEvent.id;
    }

    // Handle timed events (with dateTime)
    if ('dateTime' in googleEvent.start){
        event.start = moment(googleEvent.start.dateTime).toDate()
    }
    if ('dateTime' in googleEvent.end){
        event.end = moment(googleEvent.end.dateTime).toDate()
    }

    // Handle all-day events (with date only, no time)
    // IMPORTANT: All-day events from Google are timezone-agnostic
    // "2026-01-02" means January 2nd regardless of timezone
    // We parse using UTC to avoid timezone shifts, then adjust for local display
    if ('date' in googleEvent.start){
        // Parse as UTC midnight, then create a local date for that same calendar date
        const [year, month, day] = googleEvent.start.date.split('-').map(Number);
        event.start = new Date(year, month - 1, day, 0, 0, 0); // Local midnight
        event.isAllDay = true
    }
    if ('date' in googleEvent.end){
        // Google uses EXCLUSIVE end dates for all-day events
        // end="2026-01-03" means the event ends at the START of Jan 3 (i.e., end of Jan 2)
        const [year, month, day] = googleEvent.end.date.split('-').map(Number);
        // Create date for the end day at local midnight (exclusive)
        event.end = new Date(year, month - 1, day, 0, 0, 0);
        event.isAllDay = true
    }
    
    if (googleEvent.extendedProperties && googleEvent.extendedProperties.private){
        event = {...event, ...googleEvent.extendedProperties.private}
    }
    else {
        event = {...event}
    }

    for (const prop of jsonProperties) {
        if (Object.hasOwn(event, prop)) {
            event[prop] = parsePrivateValue(prop, event[prop]);
        }
    }
    
    for (const prop of boolProperties) {
        if (Object.hasOwn(event, prop) && !(typeof event[prop] == "boolean")) {
            event[prop] = (event[prop]  === 'true');
        }
    }
    for (const prop of numericProperties) {
        if (event[prop] !== undefined && event[prop] !== null) {
            const numeric = Number(event[prop]);
            event[prop] = Number.isNaN(numeric) ? null : numeric;
        }
    }

    // Extract Google Calendar attendees for inbound attendee sync (Phase 3)
    if (googleEvent.attendees && googleEvent.attendees.length > 0) {
        event.googleAttendees = googleEvent.attendees.map(a => ({
            email: a.email,
            displayName: a.displayName || null,
            responseStatus: a.responseStatus,
            self: a.self || false,
            organizer: a.organizer || false,
        }));
    }

    return event;
}

export const googleCreateNewEvent = async (event, calendarId='primary') => {
    // Get token for the account that owns this calendar
    const accountEmail = event.primary_email;
    const userAccessToken = getTokenForAccount(accountEmail);

    if (!userAccessToken) {
        logger.error(`No access token found for account: ${accountEmail}`);
        return { error: { message: `Not authenticated for account ${accountEmail}`, needsReauth: true } };
    }

    const googleEvent = convertGrownupsEventToGoogleEvent(event);
    const googleCalendarEvent = await insertEvent(userAccessToken, googleEvent, calendarId);
    if (googleCalendarEvent && 'error' in googleCalendarEvent){
        // Check for token expiration
        handleTokenExpiredError(accountEmail, googleCalendarEvent.error);
        return googleCalendarEvent;
    }
    const grownupsEvent = convertGoogleEventToGrownupsEvent(googleCalendarEvent);
    // Preserve the account info from the original event
    grownupsEvent.primary_email = accountEmail;
    grownupsEvent.calendar = calendarId;
    return grownupsEvent;
}


export const googleUpdateEvent = async (event) => {
    // Get token for the account that owns this event
    const accountEmail = event.primary_email;
    const userAccessToken = getTokenForAccount(accountEmail);

    if (!userAccessToken) {
        logger.error(`No access token found for account: ${accountEmail}`);
        return { error: { message: `Not authenticated for account ${accountEmail}`, needsReauth: true } };
    }

    const googleEvent = convertGrownupsEventToGoogleEvent(event);
    // Add id for updates (not included in conversion since inserts shouldn't have it)
    googleEvent.id = event.id;
    const googleCalendarEvent = await updateEvent(userAccessToken, googleEvent, event.calendar);
    if (googleCalendarEvent && 'error' in googleCalendarEvent){
        // Check for token expiration
        handleTokenExpiredError(accountEmail, googleCalendarEvent.error);
        return googleCalendarEvent;
    }
    const grownupsEvent = convertGoogleEventToGrownupsEvent(googleCalendarEvent);
    // Preserve the account info from the original event
    grownupsEvent.primary_email = accountEmail;
    grownupsEvent.calendar = event.calendar;
    return grownupsEvent;
}

export const googleDeleteEvent = async (event) => {
    // Get token for the account that owns this event
    const accountEmail = event.primary_email;
    const userAccessToken = getTokenForAccount(accountEmail);

    if (!userAccessToken) {
        logger.error(`No access token found for account: ${accountEmail}`);
        return { success: false, error: { message: `Not authenticated for account ${accountEmail}`, needsReauth: true } };
    }

    const result = await deleteEvent(userAccessToken, event.id, event.calendar);

    // Check for token expiration on failure
    if (!result.success) {
        handleTokenExpiredError(accountEmail, result.error);
    }

    return result;
}

/**
 * Push an attendee list to a Google Calendar event.
 * Used by the outbound RSVP sync to update the host's Google Calendar.
 *
 * @param {string} googleEventId - The Google Calendar event ID
 * @param {string} calendarId - The Google Calendar ID (e.g. 'primary')
 * @param {Array<{email: string, responseStatus: string, displayName?: string}>} attendees
 * @param {string} accountEmail - The Google account email that owns the calendar
 * @returns {Promise<{success: boolean, error?: string, needsReauth?: boolean}>}
 */
export const googlePushAttendees = async (googleEventId, calendarId, attendees, accountEmail) => {
    const userAccessToken = getTokenForAccount(accountEmail);

    if (!userAccessToken) {
        logger.error(`No access token for account: ${accountEmail}`);
        return { success: false, error: 'no_token', needsReauth: true };
    }

    const result = await patchEventAttendees(userAccessToken, googleEventId, calendarId, attendees);

    if (result.success) {
        maybeExtendToken(accountEmail, userAccessToken);
        return result;
    }

    // Handle specific error cases
    if (result.status === 404) {
        return { success: false, error: 'event_not_found', status: 404 };
    }

    const expired = handleTokenExpiredError(accountEmail, { status: result.status });
    if (expired.tokenExpired) {
        return { success: false, error: 'token_expired', needsReauth: true };
    }

    return result;
}
